import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { ToolBarItem } from '@sdflc/ui';

import {
  logger,
  OperationManager,
  OPERATIONS,
  onlyPropsOfObject,
} from '../../../utils';
import { ACTIONS_BUTTONS, ACTIONS_IDS } from 'config';
import { useApiUserSubscribeListItem } from '../../../graphql/hooks';
import { LocalizationContext } from '../../../contexts';

import { userSubscribeListItemForm } from './userSubscribeListItemForm';

const BUTTONS = ACTIONS_BUTTONS({ withNames: true });

const tplNewUserSubscribeListItem = {
  subscribedForUserId: null,
  status: 100,
};

const useUserSubscribeListItems = () => {
  const { getText } = useContext(LocalizationContext);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const {
    userSubscribeListItem,
    userSubscribeListItems,
    userSubscribeListItemList,
    userSubscribeListItemCreate,
    userSubscribeListItemUpdate,
    userSubscribeListItemRemoveMany,
    userSubscribeListItemsOpsManager,
  } = useApiUserSubscribeListItem();

  const contactId = formData?.id || '';
  const createOp = userSubscribeListItemsOpsManager.getCreateOp();
  const updateOp = userSubscribeListItemsOpsManager.get(
    contactId,
    OPERATIONS.UPDATE
  );
  const saveOp = contactId ? updateOp : createOp;
  const saveOpResult = saveOp.result;
  const updatingOps = userSubscribeListItemsOpsManager.getOpValues(
    OPERATIONS.UPDATE
  );
  const removingOps = userSubscribeListItemsOpsManager.getOpValues(
    OPERATIONS.REMOVE_MANY
  );

  useEffect(() => {
    setOpen(!!formData);
  }, [formData]);

  useEffect(() => {
    // This effect should close the app drawer on successfull saving of new item

    // Item is not being saved yet
    if (!createOp.called) {
      return;
    }

    // Create operation result is not in progress and succeeded
    if (!createOp.result.isInProgress() && createOp.result.didSucceed()) {
      userSubscribeListItemsOpsManager.reset(
        OperationManager.defaultContext,
        OPERATIONS.CREATE
      );
      setFormData(null);
    }
  }, [createOp, userSubscribeListItemsOpsManager]);

  useEffect(() => {
    // This effect should close the app drawer on successfull saving of exising item

    // Item is not being saved yet
    if (!updateOp.called) {
      return;
    }

    // Operation result is not in progress and succeeded
    if (!updateOp.result.isInProgress() && updateOp.result.didSucceed()) {
      userSubscribeListItemsOpsManager.reset(contactId, OPERATIONS.UPDATE);
      setFormData(null);
    }
  }, [updateOp, userSubscribeListItemsOpsManager, contactId]);

  const handleClickAction = useCallback(
    async (action, item) => {
      switch (action.id) {
        default:
          logger.warn(
            'The "useUserSubscribeListItems" got an unexpected action:',
            {
              action,
              item,
            }
          );
          break;

        case ACTIONS_IDS.ADD:
        case ACTIONS_IDS.CREATE:
          userSubscribeListItemsOpsManager.reset(
            OperationManager.defaultContext,
            OPERATIONS.SET
          );
          setFormData(cloneDeep(tplNewUserSubscribeListItem));
          break;

        case ACTIONS_IDS.SAVE:
          if (!item.id) {
            userSubscribeListItemCreate({ params: item });
          } else {
            userSubscribeListItemUpdate({
              where: { id: item.id },
              params: onlyPropsOfObject(item, tplNewUserSubscribeListItem),
            });
          }
          break;

        case ACTIONS_IDS.CANCEL:
          setFormData(null);
          break;

        case ACTIONS_IDS.EDIT:
          userSubscribeListItemsOpsManager.reset(
            OperationManager.defaultContext,
            OPERATIONS.UPDATE
          );
          setFormData(cloneDeep(item));
          break;

        case ACTIONS_IDS.REMOVE:
          userSubscribeListItemRemoveMany({ where: [{ id: item.id }] });
          break;

        case ACTIONS_IDS.TOGGLE_STATUS:
          {
            const toUpd = onlyPropsOfObject(item, tplNewUserSubscribeListItem);
            toUpd.status = item.status === 100 ? 0 : 100;
            userSubscribeListItemUpdate({
              where: { id: item.id },
              params: toUpd,
            });
          }
          break;
      }
    },
    [
      userSubscribeListItemCreate,
      userSubscribeListItemUpdate,
      userSubscribeListItemRemoveMany,
      userSubscribeListItemsOpsManager,
    ]
  );

  const handleClickClose = useCallback(() => {
    handleClickAction({ id: ACTIONS_IDS.CANCEL }, null);
  }, [handleClickAction]);

  const formCfg = useMemo(() => {
    return {
      sections: userSubscribeListItemForm,
      labels: getText('blocks.userSubscribeListItem.form.labels'),
      hints: getText('pages.userSubscribeListItem.form.hints'),
      placeholders: getText('pages.userSubscribeListItem.form.placeholders'),
    };
  }, [getText]);

  const formActions = useMemo(() => [BUTTONS.SAVE, BUTTONS.CANCEL], []);
  const actionInProgress = useMemo(
    () => (saveOpResult.isInProgress() ? [ACTIONS_IDS.SAVE] : []),
    [saveOpResult]
  );

  const toolBarItems = useMemo(() => {
    return [
      // {
      //   id: 'keyword',
      //   type: ToolBarItem.types.COMPONENT,
      //   name: 'keyword',
      //   value: filters.keyword,
      //   component: Input,
      //   placeholder: getText('generic.search'),
      //   leftIcon: 'Search',
      // },
      { ...BUTTONS.ADD, type: ToolBarItem.types.BUTTON },
    ];
  }, []);

  return {
    open,
    userSubscribeListItem,
    userSubscribeListItems,
    userSubscribeListItemList,
    formData,
    formCfg,
    formActions,
    setFormData,
    handleClickAction,
    handleClickClose,
    userSubscribeListItemsOpsManager,
    actionInProgress,
    toolBarItems,
    saveOp,
    saveOpResult,
    updatingOps,
    removingOps,
  };
};

export { useUserSubscribeListItems };
